/**
 * V86 — Modernised Makeover Enquiry Form.
 *
 * Highlights:
 *   • Searchable country dropdown (flag + name + dial code).
 *   • Auto-detects time zones for the selected country and populates a
 *     second searchable dropdown; single-tz countries auto-select.
 *   • Shows current local time in 12-hour AM/PM, updated once per second.
 *   • WhatsApp field auto-prefixes the country dial code (readonly prefix
 *     + user-typed local number).
 *   • City / Goals / Additional Notes are OPTIONAL.
 *   • Required: name, country, timezone, email, whatsapp, gender, age,
 *     preferred_service, preferred_time.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Clock } from "lucide-react";
import axios from "axios";
import { MAKEOVER_LEAD_FORM_FIELDS } from "../data/makeoverData";
import { COUNTRIES, describeTimezone } from "../data/countries";
import SearchableSelect from "./ui/SearchableSelect";
import SearchableMultiSelect from "./ui/SearchableMultiSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match a MAKEOVER_LEAD_FORM_FIELDS entry by name so we can reuse the
// canonical `options` list for gender + preferred_service without
// duplicating that data here.
const fieldByName = MAKEOVER_LEAD_FORM_FIELDS.reduce((acc, f) => {
    acc[f.name] = f;
    return acc;
}, {});

const GENDER_OPTIONS = (fieldByName.gender?.options || []).map((o) => ({ value: o, label: o }));
const SERVICE_OPTIONS = (fieldByName.preferred_service?.options || []).map((o) => ({ value: o, label: o }));

// Map default-country name (e.g. "USA") to ISO-2 for the country-variant
// pages so they preselect the right country in the dropdown.
const NAME_TO_ISO = COUNTRIES.reduce((acc, c) => {
    acc[c.name.toLowerCase()] = c.iso2;
    if (c.iso2 === "US") acc["usa"] = c.iso2;
    if (c.iso2 === "GB") { acc["uk"] = c.iso2; acc["united kingdom"] = c.iso2; }
    if (c.iso2 === "AE") acc["uae"] = c.iso2;
    if (c.iso2 === "NZ") acc["new zealand"] = c.iso2;
    return acc;
}, {});

function iso2ForDefault(defaultCountry) {
    if (!defaultCountry) return "";
    return NAME_TO_ISO[defaultCountry.toLowerCase()] || "";
}

export default function MakeoverEnquiryForm({ defaultCountry, defaultService, source = "makeover-master" }) {
    const initialIso = iso2ForDefault(defaultCountry);
    const initialCountry = COUNTRIES.find((c) => c.iso2 === initialIso) || null;

    const [countryIso, setCountryIso] = useState(initialIso);
    const [timezone, setTimezone] = useState(() => (initialCountry && initialCountry.timezones.length === 1 ? initialCountry.timezones[0] : ""));
    // V175 — Preferred service is now multi-select. Seeded from an optional
    // defaultService (e.g. category variant pages) so visitors landing on
    // /online-makeover-classes/makeup-training arrive with that one pre-ticked.
    const [values, setValues] = useState({
        name: "",
        city: "",
        email: "",
        whatsappLocal: "",
        gender: "",
        age: "",
        preferred_services: defaultService ? [defaultService] : [],
        preferred_time: "",
        goals: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const country = COUNTRIES.find((c) => c.iso2 === countryIso) || null;

    // ── Live clock tick (once per second) — used for the timezone dropdown
    //    display AND the selected-tz badge below the field.
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    // When the country changes, refresh the timezone: single-tz countries
    // auto-select; multi-tz countries clear so the user must choose.
    const lastCountryRef = useRef(countryIso);
    useEffect(() => {
        if (lastCountryRef.current === countryIso) return;
        lastCountryRef.current = countryIso;
        if (!country) { setTimezone(""); return; }
        setTimezone(country.timezones.length === 1 ? country.timezones[0] : "");
    }, [countryIso, country]);

    // ── Country dropdown options
    const countryOptions = useMemo(
        () => COUNTRIES.map((c) => ({
            value: c.iso2,
            label: `${c.flag}  ${c.name}  (${c.dial})`,
            keywords: `${c.name} ${c.iso2} ${c.dial}`,
            _country: c,
        })),
        []
    );

    // ── Timezone dropdown options for the selected country (live clock)
    const timezoneOptions = useMemo(() => {
        if (!country) return [];
        return country.timezones.map((tz) => {
            const d = describeTimezone(tz, now);
            return {
                value: tz,
                label: `${country.name} · ${d.city} (${d.abbr}) — ${d.currentTime}`,
                keywords: `${d.city} ${d.abbr} ${tz}`,
            };
        });
    }, [country, now]);

    const tzInfo = timezone ? describeTimezone(timezone, now) : null;

    const setField = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        const errors = [];
        if (!values.name.trim()) errors.push("Full Name");
        if (!country) errors.push("Country");
        if (!timezone) errors.push("Time Zone");
        if (!values.email.trim()) errors.push("Email");
        if (!values.whatsappLocal.trim()) errors.push("WhatsApp Number");
        if (!values.gender) errors.push("Gender");
        if (!values.age) errors.push("Age");
        if (!values.preferred_services || values.preferred_services.length === 0) errors.push("Preferred Service");
        if (!values.preferred_time.trim()) errors.push("Preferred Session Time");
        if (errors.length) {
            toast.error(`${errors[0]} is required.`);
            return;
        }
        setSubmitting(true);
        try {
            const local = values.whatsappLocal.replace(/[^0-9]/g, "");
            const dial = country.dial;
            const whatsappFull = `${dial} ${local}`.trim();
            const whatsappDigits = `${dial}${local}`.replace(/[^0-9]/g, "");
            const localTime = tzInfo ? `${tzInfo.currentTime} ${tzInfo.abbr}`.trim() : "";
            const payload = {
                name: values.name.trim(),
                country: country.name,
                country_code: country.iso2,
                city: values.city.trim() || null,
                timezone,
                local_time_at_submission: localTime,
                email: values.email.trim(),
                whatsapp: whatsappDigits,
                whatsapp_country_code: dial,
                whatsapp_full: whatsappFull,
                gender: values.gender,
                age: Number(values.age) || null,
                // V175 — Multi-select: send both the array (new consumers) and
                // a comma-joined string (keeps the legacy DB field + admin
                // table + CSV working on day one without migration).
                preferred_services: values.preferred_services,
                preferred_service: values.preferred_services.join(", "),
                preferred_time: values.preferred_time.trim(),
                goals: values.goals.trim() || null,
                notes: values.notes.trim() || null,
                source,
            };
            await axios.post(`${API}/makeover/enquiry`, payload);
            toast.success("Enquiry received. Our team will WhatsApp you within 12 hours.");
            setValues({
                name: "", city: "", email: "", whatsappLocal: "", gender: "", age: "",
                preferred_services: defaultService ? [defaultService] : [],
                preferred_time: "", goals: "", notes: "",
            });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong — please try again.";
            toast.error(typeof msg === "string" ? msg : "Please check the form and retry.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form onSubmit={submit} data-testid="makeover-enquiry-form" className="space-y-3 bg-white p-5 rounded-2xl border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-1">Book your 1-on-1 slot</div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">Talk to a makeover coach</h3>

            <div className="grid sm:grid-cols-2 gap-3">
                {/* Full Name */}
                <Field label="Full Name" required>
                    <input
                        type="text" data-testid="makeover-field-name"
                        value={values.name} onChange={setField("name")}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* Country */}
                <Field label="Country" required>
                    <SearchableSelect
                        options={countryOptions}
                        value={countryIso}
                        onChange={setCountryIso}
                        placeholder="Search 200+ countries…"
                        data-testid="makeover-field-country"
                        ariaLabel="Country"
                        renderSelected={(o) => (
                            <span className="flex items-center gap-2 truncate">
                                <span className="text-base leading-none">{o._country.flag}</span>
                                <span className="truncate">{o._country.name}</span>
                                <span className="text-[11px] text-slate-500 shrink-0">({o._country.dial})</span>
                            </span>
                        )}
                        renderOption={(o) => (
                            <span className="flex items-center gap-2 truncate">
                                <span className="text-base leading-none shrink-0 w-6">{o._country.flag}</span>
                                <span className="truncate">{o._country.name}</span>
                                <span className="ml-auto text-[11px] text-slate-400 shrink-0">{o._country.dial}</span>
                            </span>
                        )}
                    />
                </Field>

                {/* City — optional */}
                <Field label="City" optional>
                    <input
                        type="text" data-testid="makeover-field-city"
                        value={values.city} onChange={setField("city")}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* Time Zone (auto-populated from country) */}
                <Field label="Time Zone" required>
                    <SearchableSelect
                        options={timezoneOptions}
                        value={timezone}
                        onChange={setTimezone}
                        disabled={!country || country.timezones.length <= 1}
                        placeholder={country ? "Select a time zone" : "Select country first"}
                        data-testid="makeover-field-timezone"
                        ariaLabel="Time Zone"
                        renderSelected={(o) => <span className="truncate">{o.label}</span>}
                        emptyText="No time zone matches"
                    />
                    {tzInfo && (
                        <div data-testid="makeover-tz-live-clock" className="mt-1.5 text-[11px] text-slate-600 inline-flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-rose-500" />
                            <span><strong className="text-slate-800">{tzInfo.city}</strong> ({tzInfo.abbr}) — <span className="tabular-nums">{tzInfo.currentTime}</span> local time</span>
                        </div>
                    )}
                </Field>

                {/* Email */}
                <Field label="Email" required>
                    <input
                        type="email" data-testid="makeover-field-email"
                        value={values.email} onChange={setField("email")}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* WhatsApp Number */}
                <Field label="WhatsApp Number" required>
                    <div className="flex">
                        <span
                            data-testid="makeover-whatsapp-prefix"
                            className="inline-flex items-center px-2.5 py-2 bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg text-sm text-slate-700 font-semibold min-w-[64px] justify-center"
                        >
                            {country ? country.dial : "+—"}
                        </span>
                        <input
                            type="tel"
                            data-testid="makeover-field-whatsapp"
                            value={values.whatsappLocal}
                            onChange={setField("whatsappLocal")}
                            placeholder={country ? "Local number" : "Select country first"}
                            disabled={!country}
                            className="w-full px-3 py-2 border border-slate-200 rounded-r-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                        />
                    </div>
                </Field>

                {/* Gender */}
                <Field label="Gender" required>
                    <SearchableSelect
                        options={GENDER_OPTIONS}
                        value={values.gender}
                        onChange={(v) => setValues((s) => ({ ...s, gender: v }))}
                        placeholder="Select…"
                        data-testid="makeover-field-gender"
                        ariaLabel="Gender"
                    />
                </Field>

                {/* Age */}
                <Field label="Age" required>
                    <input
                        type="number" min={13} max={99}
                        data-testid="makeover-field-age"
                        value={values.age} onChange={setField("age")}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* Preferred Service — V175: multi-select */}
                <Field label="Preferred Services" required>
                    <SearchableMultiSelect
                        options={SERVICE_OPTIONS}
                        values={values.preferred_services}
                        onChange={(v) => setValues((s) => ({ ...s, preferred_services: v }))}
                        placeholder="Pick one or more services…"
                        data-testid="makeover-field-preferred_service"
                        ariaLabel="Preferred Services"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">Select every service you'd like covered — you can pick multiple.</p>
                </Field>

                {/* Preferred Session Time */}
                <Field label="Preferred Session Time" required>
                    <input
                        type="text" data-testid="makeover-field-preferred_time"
                        value={values.preferred_time} onChange={setField("preferred_time")}
                        placeholder="e.g. Weekday evenings 7-9 PM"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* Goals — optional */}
                <Field label="Your Goals" optional className="sm:col-span-2">
                    <textarea
                        rows={2}
                        data-testid="makeover-field-goals"
                        value={values.goals} onChange={setField("goals")}
                        placeholder="Optional — what outcome do you want?"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>

                {/* Notes — optional */}
                <Field label="Additional Notes" optional className="sm:col-span-2">
                    <textarea
                        rows={2}
                        data-testid="makeover-field-notes"
                        value={values.notes} onChange={setField("notes")}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                    />
                </Field>
            </div>

            <button
                type="submit"
                data-testid="makeover-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-3 rounded-lg text-sm disabled:opacity-60"
            >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Enquiry
            </button>
            <p className="text-[11px] text-slate-500 text-center">Our team will reach out via WhatsApp within 12 hours to schedule your 1-on-1 consultation.</p>
        </form>
    );
}

function Field({ label, required, optional, children, className = "" }) {
    return (
        <div className={className}>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
                {label}
                {required && <span className="text-rose-500 ml-0.5">*</span>}
                {optional && <span className="text-slate-400 ml-1 font-normal">(optional)</span>}
            </label>
            {children}
        </div>
    );
}
